'use client';

import { useState, useTransition } from 'react';
import { useRouter } from 'next/navigation';
import type { UserLanguage } from '@bts-web/data-layer/server';
import { useAppNotification } from '../../notifications/NotificationContextProvider';
import { postUpdateBusinessUser } from '../../common/gqlActions/postUpdateBusinessUser.action';
import { postUpdateUser } from '../../common/gqlActions/postUpdateUser.action';
import { UserTypes } from '../../common/userinfo/types';
import {
  Language,
  RTL_LANGUAGES,
  setClientLanguage,
} from '@bts-web/utils-lokalise';
import { useUserInfoContext } from '../../common/userinfo/UserInfoContext/UserInfoProvider';

export const useChangeLanguage = ({
  translations,
}: {
  translations: {
    errorTitle: string;
    errorSubtitle: string;
  };
}) => {
  const router = useRouter();

  const { data: userData, updateUserData } = useUserInfoContext();

  const { setAppNotification } = useAppNotification();

  const [isApiLoading, setIsApiLoading] = useState(false);

  const [isPending, startTransition] = useTransition();

  const isUserUpdateLoading = isApiLoading || isPending;

  const updateUserAction =
    userData.userType === UserTypes.BUSINESS_USER
      ? postUpdateBusinessUser
      : postUpdateUser;

  const onChangeLanguage = async (newLanguage: string) => {
    if (isUserUpdateLoading) return;

    try {
      setIsApiLoading(true);

      await updateUserAction({
        language: newLanguage.toUpperCase() as UserLanguage,
      });

      updateUserData({ language: newLanguage as Language });

      setClientLanguage(newLanguage);

      const htmlElement = document.querySelector('html');

      if (htmlElement) {
        const isRTL = RTL_LANGUAGES.includes(newLanguage as Language);

        htmlElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr');

        htmlElement.setAttribute('lang', newLanguage);
      }

      setIsApiLoading(false);

      startTransition(() => {
        router.refresh();
      });
    } catch (error) {
      setIsApiLoading(false);

      setAppNotification({
        title: translations.errorTitle,
        subtitle: translations.errorSubtitle,
        withClose: true,
        visual: 'error',
      });

      console.error('[ERROR][change language]', error);
    }
  };

  return {
    isUserUpdateLoading,
    onChangeLanguage,
  };
};
